import React, { useState, useEffect, useRef } from "react";
import adminService from "../../api/services/adminService";
import uploadService from "../../api/services/uploadService";
import csrOutreachService from "../../api/services/csrOutreachService";
import { FormSkeleton } from "../common/skeletons";
import { notifyApprovalsPendingChanged } from "../../utils/approvalsEvents";
import { uploadPaymentProofFiles } from "../../utils/paymentProofUrls";
import PaymentProofAttachments from "./PaymentProofAttachments";

const inputClass =
  "w-full p-2.5 text-xs md:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700 min-w-0";

const CURRENCIES = ["PKR", "AED", "USD", "EUR", "GBP"];

/**
 * CSR manual subscription: payment proof required. Notes optional.
 * Submits for CSR Team Lead approval (not instant purchase).
 */
export default function CsrManualSubscriptionSection() {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [amount, setAmount] = useState("");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [currency, setCurrency] = useState("PKR");
  const [notes, setNotes] = useState("");
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [proofItems, setProofItems] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminService.getSubscriptionPlans();
        if (!cancelled && res?.plans?.length) {
          setPlans(res.plans);
          if (!subscriptionId && res.plans[0]?.id) {
            setSubscriptionId(String(res.plans[0].id));
          }
        }
      } catch {
        if (!cancelled) setError("Failed to load subscription plans");
      } finally {
        if (!cancelled) setLoadingPlans(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      proofItems.forEach((it) => {
        if (it?.preview) URL.revokeObjectURL(it.preview);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- revoke leftover blobs on unmount only
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true);
      setError(null);
      try {
        const res = await adminService.searchUsersForSubscription(searchQuery.trim());
        setSearchResults(res?.users || []);
        setShowDropdown(true);
      } catch (e) {
        setSearchResults([]);
        setError(e?.message || "Search failed");
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchQuery(user.email || "");
    setShowDropdown(false);
    setSearchResults([]);
    setError(null);
  };

  const handleClearUser = () => {
    setSelectedUser(null);
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!selectedUser?.id) {
      setError("Select a user from search results.");
      return;
    }
    const amt = parseFloat(amount);
    if (!amount || Number.isNaN(amt) || amt <= 0) {
      setError("Enter a valid amount.");
      return;
    }
    if (!currency.trim()) {
      setError("Select a currency.");
      return;
    }
    const planId = subscriptionId ? parseInt(subscriptionId, 10) : null;
    if (!planId || !plans.some((p) => p.id === planId)) {
      setError("Select a subscription plan.");
      return;
    }
    if (!proofItems.length) {
      setError("At least one payment proof attachment is required.");
      return;
    }

    setSubmitting(true);
    try {
      const payment_proof_urls = await uploadPaymentProofFiles(
        proofItems.map((it) => it.file),
        uploadService.single
      );
      if (!payment_proof_urls.length) {
        setError("Failed to upload payment proof.");
        return;
      }

      const res = await csrOutreachService.createManualSubscriptionRequest({
        user_id: selectedUser.id,
        amount: amt,
        currency,
        subscription_plan_id: planId,
        payment_proof_url: payment_proof_urls[0],
        payment_proof_urls,
        notes: notes.trim() || undefined,
      });

      setSuccessMessage(
        res?.message ||
          (res?.updated
            ? "Existing pending request updated. Your team lead will see the latest payment details."
            : "Request submitted. Your team lead will review and approve before the subscription is activated.")
      );
      setAmount("");
      setNotes("");
      proofItems.forEach((it) => {
        if (it?.preview) URL.revokeObjectURL(it.preview);
      });
      setProofItems([]);
      setSelectedUser(null);
      setSearchQuery("");
      if (!res?.updated) {
        notifyApprovalsPendingChanged({ delta: 1 });
      } else {
        notifyApprovalsPendingChanged();
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to submit request."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden mb-6">
      <div className="p-5 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-rose-50 to-white">
        <h2 className="text-lg sm:text-xl font-bold text-gray-800 tracking-tight">
          Manual subscription request
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          CSR agents: search a user, complete all payment fields, and upload one or more proof
          screenshots. Your CSR Team Lead must approve before the subscription is activated. The
          submitting CSR agent name is recorded on approval.
        </p>
      </div>
      <div className="p-5 sm:p-6">
        {loadingPlans ? (
          <FormSkeleton fields={7} />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 max-w-2xl">
            <div className="relative" ref={dropdownRef}>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                User (name, email, or phone) *
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                placeholder="Type to search..."
                className={inputClass}
                required
              />
              {selectedUser && (
                <button
                  type="button"
                  onClick={handleClearUser}
                  className="absolute right-2 top-9 text-gray-400 hover:text-red-600 text-sm"
                >
                  Clear
                </button>
              )}
              {searching && <p className="mt-1 text-xs text-gray-500">Searching…</p>}
              {showDropdown && searchResults.length > 0 && (
                <ul className="absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg py-1">
                  {searchResults.map((u) => (
                    <li key={u.id}>
                      <button
                        type="button"
                        onClick={() => handleSelectUser(u)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                      >
                        <span className="font-medium text-gray-900">
                          {u.firstName} {u.lastName}
                        </span>
                        <span className="text-gray-500 block truncate">{u.email}</span>
                        {u.contactNo && (
                          <span className="text-gray-400 text-xs">{u.contactNo}</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Amount *</label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Currency *</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className={inputClass}
                  required
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Subscription plan *
              </label>
              <select
                value={subscriptionId}
                onChange={(e) => setSubscriptionId(e.target.value)}
                className={inputClass}
                required
              >
                <option value="">Select plan</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.duration_days ? `(${p.duration_days} days)` : ""}
                  </option>
                ))}
              </select>
            </div>

            <PaymentProofAttachments items={proofItems} onChange={setProofItems} />

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Payment notes <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Bank transfer reference, transaction ID, date…"
                className={`${inputClass} min-h-[80px]`}
              />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}
            {successMessage && <p className="text-sm text-green-600">{successMessage}</p>}

            <button
              type="submit"
              disabled={submitting || loadingPlans}
              className="px-4 sm:px-6 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-semibold shadow-md transition-colors"
            >
              {submitting ? "Submitting request…" : "Submit for team lead approval"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
